import { usOrderNeedsFillVisibility } from '../../lib/polymarketUs/fillProgress';
/**
 * #1226 — Polymarket US as rows of the Position Tracker's shared lists.
 *
 * The account is provenance, never a gate (Brendan, Oct 16): every US account
 * the trader can view as their own is read here, one reader each, and its
 * working orders and held positions become rows of the Tracker's Open Orders
 * and Open Positions beside the Kalshi and international ones. Nothing here
 * renders a row; the Tracker builds the shared view-models from what each
 * reader reports (`UsAccountView`).
 *
 * What each reader keeps, exactly as the former US section did
 * (`UsPortfolioPanel` / `UsPortfolioWorkspace`, #989/#1006):
 * - reads fenced by the account visit: a context opened later is a new
 *   visit, and a read or action from an earlier one never paints;
 * - one operation in flight per account (cancel, repeat cancel, check
 *   status, refresh), the lock closed before React re-renders;
 * - a working-only order list, a finished order kept one read with its word;
 * - holdings with something in them (known-zero and phantom rows hidden);
 * - confirm-before-cancel through the Tracker's confirm dialog;
 * - the Close guard (`closeState`): no second Close on a holding until its
 *   size changes, no Close over an open sell, none on a stale read;
 * - the Edit/Close window bound to the visit the row was read under.
 *
 * The account client answers one read at a time, so contexts open in turn;
 * each reader has its own portfolio client (the browser client fences every
 * read to one selected account).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ConfirmModal } from '@/components/atoms/Modal';
import type { UsAccountClient, UsAccountContext, UsVisibleAccount } from '@/lib/polymarketUs/accountClient';
import { createUsBrowserAccountClient } from '@/lib/polymarketUs/browserAccountClient';
import { createUsBrowserPortfolioClient } from '@/lib/polymarketUs/browserPortfolioClient';
import type { UsMarketServed } from '@/lib/polymarketUs/marketServing';
import type { UsPortfolioClient } from '@/lib/polymarketUs/portfolioClient';
import {
  acceptUsSnapshot,
  applyUsCancelResult,
  applyUsOrderStatus,
  presentUsOrder,
  presentUsPosition,
  usCancelView,
  usOrderKey,
  usPositionKey,
} from '@/lib/polymarketUs/portfolioModel';
import { decimalUnits } from '@/lib/polymarketUs/ticketModel';
import type { UsCloseEvent, UsOrderRow, UsPortfolioSnapshot, UsPositionRow, UsReadiness, UsScope } from '@/lib/polymarketUs/types';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { finishedSince, usCancelConfirmText, usKnownZeroHolding, usPhantomHolding } from '../PolymarketUs/UsPortfolioPanel';
import { usAccountLabel } from '../PolymarketUs/usVenueLabel';
import type { GameRow } from '../ValueDashboard/types';
import type { TradeIntent } from '../ValueDashboard/GameDetailPanel';
import { UsActionWindow, type UsActionMount } from './UsActionWindow';
import { US_BULK_MAX_ACCOUNT_PAGES } from './usBulkCancelModel';
import { usHoldingPinKey, usOrderPinKey } from './pinIdentity';
import { usReadiness, type UsReadinessInput, type VenueReadiness } from './venueReadiness';

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);
const LOADING: UsReadinessInput = { status: 'loading', accountCount: 0, hasMore: false, error: null };
const errorCode = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';
const failureReason = (error: unknown): string => error instanceof Error && !('code' in error) && error.message
  ? error.message : errorCode(error);

/** The current US session key, read outside render (e.g. between the awaits
 * of a bulk sweep). Same shape as `useUsSessionKey`. */
export function readUsSessionKey(): string {
  const user = useAuthStore.getState().user;
  return `${getAuthSessionEpoch()}:${user?.id ?? ''}:${user?.orgId ?? ''}`;
}

/** Identity a US read belongs to. A new login (same user included), a user or
 * an org change is a new key: the previous account's rows are discarded, not
 * repainted by a late reply. Token refresh keeps the key. */
function useUsSessionKey(): string {
  const epoch = useSyncExternalStore(subscribeAuth, getAuthSessionEpoch);
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const orgId = useAuthStore((s) => s.user?.orgId ?? '');
  return `${epoch}:${userId}:${orgId}`;
}

/** Tracker admission for US: one read of the first visible-accounts page per
 * session key. Uses its own account client so it can never invalidate the
 * feed's reads (each client fences only its own generation). */
export function useUsTrackerReadiness(): { readiness: VenueReadiness; sessionKey: string; retry: () => void; retrying: boolean } {
  const sessionKey = useUsSessionKey();
  // Bumped only by an explicit Retry: a failed read is never retried on its own.
  const [attempt, setAttempt] = useState(0);
  const [read, setRead] = useState<{ key: string; attempt: number; input: UsReadinessInput } | null>(null);
  useEffect(() => {
    const client = createUsBrowserAccountClient();
    let current = true;
    client.listAccounts().then(
      (page) => {
        if (current) setRead({ key: sessionKey, attempt, input: {
          status: 'ready', accountCount: page.accounts.length, hasMore: page.nextCursor !== null, error: null,
        } });
      },
      (error) => {
        if (current) setRead({ key: sessionKey, attempt, input: {
          status: 'error', accountCount: 0, hasMore: false, error: errorCode(error),
        } });
      },
    );
    return () => {
      current = false;
      client.invalidate();
    };
  }, [sessionKey, attempt]);
  // A read from a previous session key is never shown now. #1060: during an
  // explicit Retry the same session's last answer stands until the new one
  // lands; showing "loading" instead removed the notice line (and the Retry
  // button under the pointer) and brought it back when the read answered.
  const sameSession = read?.key === sessionKey;
  const input = sameSession ? read.input : LOADING;
  const retrying = sameSession && read.attempt !== attempt;
  const readiness = useMemo(() => usReadiness(input), [input]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { readiness, sessionKey, retry, retrying };
}

/** #1006: the Tracker opens its order panel on this game, its US tab on the
 * row's exact outcome and account. `live` re-derives that panel from the
 * current board (`usPanelTarget`); `marketSlug`/`href` name it if the game
 * leaves the board while the panel is open. */
export type UsOpenPanel = (panel: { game: GameRow; intent: TradeIntent; live(): GameRow | null;
  marketSlug: string; href: string | null }) => void;

/** #1006: a US row the panel cannot open on (a spread, a period total, a
 * market not yet or never matched to exactly one board game). The Tracker
 * closes its previous panel and names the market, with the exact game page
 * when there is one, as it does for an international order. */
export type UsUnavailablePanel = (book: { marketSlug: string; href: string | null }) => void;

/** Hover text on such a row (the one that opens says US_ROW_OPEN_TITLE).
 * "Right now": a market whose catalogue lookup is pending or failed is here
 * too, until it answers. */
export const US_ROW_NO_PANEL_TITLE = 'No order panel for this market right now: clicking says so and closes any open panel';

/** #1034's rail cadence: the Tracker's US rows re-read this often while the
 * page is visible, as its Kalshi and international lists stay live. */
export const US_TRACKER_POLL_MS = 10_000;

/** A row action a reader runs against its account (one at a time). */
export type UsRowOperation = 'cancel' | 'repeat' | 'status';

/** What a holding's Close shows: a button for `quantity`, a reason, or nothing. */
export type UsCloseState = { quantity: string } | { reason: string } | null;

/** The stable per-account handlers the Tracker's rows call. */
export type UsAccountActions = {
  run(kind: UsRowOperation, row: UsOrderRow): void;
  /** Open the US ticket's edit mode (#1017) on this resting order. */
  edit(row: UsOrderRow): void;
  /** Open the US Close window: sell `quantity` of this holding. */
  close(position: UsPositionRow, quantity: string): void;
  /** Re-read the account now (an explicit Retry after a failed read). */
  refresh(): void;
  /** Re-open the account's context after a refused one. */
  retryOpen(): void;
};

/** One own-view account as its reader sees it: identity, the context it
 * opened, the read on screen, the rows that read puts in the shared lists,
 * the one operation in flight and the Close verdicts. Data only, so the
 * Tracker's view-models stay comparable by value; the handlers are stable. */
export type UsAccountView = {
  accountId: string;
  ownership: 'personal' | 'pooled';
  /** `Account n · desk/personal` with two or more own-view accounts; null with one. */
  label: string | null;
  /** The opened context (scope, readiness, permissions); null while opening or refused. */
  context: UsAccountContext | null;
  /** The visit the context belongs to; a re-opened context is a new visit. */
  visit: number;
  opening: boolean;
  /** The context read failed (code); null otherwise. */
  openError: string | null;
  /** The last read accepted in this visit; a failed refresh keeps it. */
  snapshot: UsPortfolioSnapshot | null;
  /** A read is in flight (the first, or a refresh that keeps the rows). */
  loading: boolean;
  /** The last read failed (code); with a snapshot, that read is stale. */
  readError: string | null;
  /** Working orders only, plus a finished one kept this read with its word
   * and any carrying a row error (the former section's list). */
  workingOrders: readonly UsOrderRow[];
  /** Holdings with something in them (known-zero and phantom rows hidden). */
  openPositions: readonly UsPositionRow[];
  /** Any operation or read in flight: every action on the account waits. */
  busy: boolean;
  /** The one operation in flight, by `usOrderKey`. */
  pending: { key: string; kind: UsRowOperation | 'refresh' } | null;
  /** Last failure per `usOrderKey`; the row itself is never removed for it. */
  errors: ReadonlyMap<string, string>;
  /** The reader's clock sample for the repeat-cancel wait. */
  nowMs: number;
  /** What each holding's Close shows, by `usPositionKey`. */
  closeStates: ReadonlyMap<string, UsCloseState>;
  actions: UsAccountActions;
};
/** The view's data alone; the reader joins its stable actions to it when it
 * reports (outside render: the handlers read the reader's refs). */
export type UsAccountState = Omit<UsAccountView, 'actions'>;

/** The pooled US read the Tracker builds its US rows from. */
export type UsTrackerRead = {
  /** Own-view accounts in list order, each present once its reader has mounted. */
  accounts: readonly UsAccountView[];
  /** How many own-view accounts the list holds (0 once listed = nothing is
   * read here: every visible account is desk-only, its rows on Org Detail). */
  ownAccounts: number;
  list: { state: 'loading' } | { state: 'listed'; complete: boolean } | { state: 'failed'; code: string };
  retryList(): void;
};

type Portfolio = Pick<UsPortfolioClient, 'setSelection' | 'readPortfolio' | 'readOrderPage' | 'cancelOrder' | 'checkOrderStatus' | 'repeatCancel'>;
type Accounts = Pick<UsAccountClient, 'listAccounts' | 'selectAccount' | 'invalidate'>;

type Read = {
  visit: number;
  snapshot: UsPortfolioSnapshot | null;
  loading: boolean;
  error: string | null;
  /** The refresh signal this read answered (a newer one is a read in flight). */
  signal: number;
  after?: string;
  /** Intent ids that finished under the trader's eyes in this read (working
   * in the previous one, terminal now): shown once more with their terminal
   * word, so the outcome of a cancel is seen at the control. */
  keep?: ReadonlySet<string>;
};
type Pending = { key: string; kind: UsRowOperation | 'refresh'; visit: number };
type UsAction = { kind: 'edit'; order: UsOrderRow } | { kind: 'close'; position: UsPositionRow; quantity: string };
/** A close this account started: the holding's size then, and its order. */
type ClosedHolding = { size: string; intentId: string | null };
const NO_ERRORS: ReadonlyMap<string, string> = new Map();
const NO_CLOSE: ReadonlyMap<string, UsCloseState> = new Map();
const NO_ROWS: readonly never[] = [];

const NOT_READ = 'Close unavailable: open orders not fully read';
const OPEN_SELL = 'Close unavailable: you have an open sell on this holding. Edit or cancel it first.';
/** #1006: the ticket could not read this market, so its Close could not be checked or placed. */
const NOT_SERVED = 'Close unavailable: no Polymarket US prices are coming in for this market. Refresh to check again.';
const closedKey = (c: Pick<UsAccountContext, 'accountId' | 'scope'>, p: UsPositionRow) =>
  `${c.accountId}|${c.scope.scopeKey}|${p.target.marketSlug}|${p.target.outcome}`;
/** A holding's size, when it is an established positive quantity the ticket
 * can read (its exact-decimal grammar); else null. */
const closableQuantity = (position: UsPositionRow): string | null => {
  const held = position.heldQuantity;
  const units = held === null ? null : decimalUnits(held);
  return presentUsPosition(position).complete && units !== null && units > 0n ? held : null;
};
const zeroFill = (order: UsOrderRow) => order.lifecycle !== null && decimalUnits(order.lifecycle.filledQuantity) === 0n;
/** A sell that filled after the holding's ledger was read: the size shown may
 * not count it yet (a reload loses the in-memory close record; this does not). */
const filledSinceLedger = (order: UsOrderRow, position: UsPositionRow) => {
  const fill = order.lifecycle === null ? null : decimalUnits(order.lifecycle.filledQuantity);
  return (fill === null || fill > 0n) && order.lifecycle !== null
    && order.lifecycle.observedAtMs > (position.ledgerReadAtMs ?? Number.NEGATIVE_INFINITY);
};

/** One rule (#1113): Edit or Close is usable exactly when there is no reason it isn't. */
function canPlace(context: UsAccountContext, side: 'buy' | 'sell'): boolean {
  return context.readiness.state === 'ready' && context.readiness.canSubmit && context.permissions[side];
}

/**
 * What a holding's Close shows (the former AccountBlock's `closeState`),
 * judged with every order of the read (unfiltered by chips). `readCurrent`:
 * the read on screen succeeded (no failed refresh). `ordersComplete`: it also
 * holds every one of the viewer's saved orders (newest page, no older page).
 */
export function usCloseStateFor(args: {
  position: UsPositionRow;
  orders: readonly UsOrderRow[];
  context: UsAccountContext;
  closed: ClosedHolding | undefined;
  readCurrent: boolean;
  ordersComplete: boolean;
  served: UsMarketServed;
}): UsCloseState {
  const { position, orders, context, closed, readCurrent, ordersComplete, served } = args;
  const quantity = closableQuantity(position);
  if (quantity === null) {
    // A known zero or short holding has nothing to sell: nothing is shown.
    return presentUsPosition(position).complete ? null : { reason: 'Close unavailable: holding size not established' };
  }
  const same = (o: UsOrderRow) => o.target.marketSlug === position.target.marketSlug && o.target.outcome === position.target.outcome;
  const ownSells = orders.filter((o) => same(o) && o.ownership === 'own' && o.action === 'sell');
  if (closed && closed.size === position.heldQuantity) {
    const own = closed.intentId === null ? undefined : orders.find((o) => o.intentId === closed.intentId);
    if (!own || !presentUsOrder(own).terminal || !zeroFill(own)) {
      return { reason: 'Close sent: waiting for this holding to update. Check your orders.' };
    }
  }
  // A failed refresh leaves this holding (and its open sells) stale.
  if (!readCurrent) return { reason: NOT_READ };
  // The relay counts your own open sells with the holding, whatever page of
  // orders is on screen. An older relay does not: then only a read of every
  // order can show there is none.
  const openSell = position.openOwnSellQuantity;
  if (openSell == null) {
    if (!ordersComplete) return { reason: NOT_READ };
  } else if (decimalUnits(openSell) !== 0n) return { reason: OPEN_SELL };
  // Your own open sell already offers (part of) this holding: a Close would sell it twice.
  if (ownSells.some((o) => !presentUsOrder(o).terminal)) return { reason: OPEN_SELL };
  // A sell filled after the size was read: wait for the size to count it.
  if (ownSells.some((o) => filledSinceLedger(o, position))) {
    return { reason: 'Close unavailable: a sell filled after this holding was read. Waiting for it to update.' };
  }
  if (!canPlace(context, 'sell')) return { reason: 'Close unavailable: selling isn’t available on this account now' };
  // The catalogue answered that this market has no current book (or isn't
  // listed): the ticket could not read it. Unknown (not read yet, or the read
  // failed) offers Close. After the account's own reason, which applies to
  // every holding and which Refresh would not change.
  if (served === 'not-served') return { reason: NOT_SERVED };
  return { quantity };
}

/**
 * One own-view account's reader: the context (opened in turn with the other
 * accounts), its reads, its one operation at a time, the confirm-before-cancel
 * request and the Edit/Close window. Returns the account's view for the
 * Tracker, and what the reader itself must render (the dialogs).
 */
export function useUsAccountRows({
  accountId, ownership, label, openContext, portfolio, refreshSignal = 0, autoRefreshMs, confirmCancel = false,
  clock = Date.now, marketServed, servingVersion = 0, pinTransfer,
}: {
  accountId: string;
  ownership: 'personal' | 'pooled';
  label: string | null;
  /** Opens the account's own-view context, in turn with the other readers. */
  openContext(accountId: string): Promise<UsAccountContext>;
  portfolio: Portfolio;
  /** A new value re-reads the account (the Tracker's Cancel all touched US orders). */
  refreshSignal?: number;
  /** Re-read this often while the page is visible; absent = on demand. */
  autoRefreshMs?: number;
  /** R8-F1: ask before a row's cancel. */
  confirmCancel?: boolean;
  clock?: () => number;
  /** #1006: whether Polymarket US is serving a holding's market now (the Close rule). */
  marketServed?: (marketSlug: string) => UsMarketServed;
  /** Bumped when a serving answer lands, so the Close verdicts re-derive. */
  servingVersion?: number;
  /** An edit replaced a pinned order: the pin moves to the replacement. */
  pinTransfer?(previousKey: string, replacementKey: string): void;
}): {
  view: UsAccountState;
  actions: UsAccountActions;
  confirm: { kind: 'cancel' | 'repeat'; row: UsOrderRow } | null;
  confirmBack(): void;
  confirmGo(): void;
  acting: UsActionMount | null;
} {
  // A context visit: every open (mount, Retry) is a new one, and a read or
  // action started in an earlier visit can never paint into this one.
  const generation = useRef(0);
  const [context, setContext] = useState<(UsAccountContext & { visit: number }) | null>(null);
  const [opening, setOpening] = useState(true);
  const [openError, setOpenError] = useState<string | null>(null);
  // Each read gets a generation; only the latest one may paint.
  const readGeneration = useRef(0);
  const [stored, setRead] = useState<Read>({ visit: 0, snapshot: null, loading: true, error: null, signal: 0 });
  const inFlight = useRef<Pending | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [storedErrors, setErrors] = useState<{ visit: number; map: ReadonlyMap<string, string> }>({ visit: 0, map: NO_ERRORS });
  const [askedCancel, setAskedCancel] = useState<{ kind: 'cancel' | 'repeat'; row: UsOrderRow; visit: number } | null>(null);
  const [nowMs, setNowMs] = useState(clock);
  /** Holdings closed from here. Close is re-offered only when a read shows a
   * different size, or shows the close's own order finished with nothing
   * filled (an empty book). A partial or full fill waits for the size, so a
   * lagging ledger can never be sold twice. */
  const [closedAt, setClosedAt] = useState<ReadonlyMap<string, ClosedHolding>>(new Map());
  const [acting, setActing] = useState<{ visit: number; action: UsAction } | null>(null);
  // Re-read after a ticket action closes: an edit or close changed the account.
  const [actionRefresh, setActionRefresh] = useState(0);
  const signal = refreshSignal + actionRefresh;

  const visit = context?.visit ?? 0;
  // Stored state from any other visit is not shown at all.
  const read: Read | null = context && stored.visit === visit ? stored : null;
  const errors = context && storedErrors.visit === visit ? storedErrors.map : NO_ERRORS;
  const pendingConfirm = askedCancel && askedCancel.visit === visit ? askedCancel : null;

  /** Read visit `v`'s portfolio as the answer to `signal`. Sets state only
   * when the reply lands: a new visit and a newer signal already read as loading. */
  const readPortfolio = useCallback((v: number, scope: UsScope, answering: number, after?: string) => {
    const mine = ++readGeneration.current;
    const request = after === undefined ? portfolio.readPortfolio(scope) : portfolio.readOrderPage(scope, after);
    return request.then(
      (snapshot) => {
        if (mine !== readGeneration.current || generation.current !== v) return;
        // The read must be for this exact scope AND view: desk rows must never
        // appear under the trader's own orders, or the reverse.
        const mismatch = !acceptUsSnapshot(scope, snapshot) ? 'US_READ_SCOPE_MISMATCH'
          : snapshot.readDetails && snapshot.readDetails.view !== 'own' ? 'US_READ_VIEW_MISMATCH' : null;
        setRead((prev) => {
          const kept = prev.visit === v ? prev.snapshot : null;
          if (!mismatch) return { visit: v, snapshot, loading: false, error: null, signal: answering, after, keep: finishedSince(kept, snapshot) };
          return { visit: v, snapshot: kept, loading: false, error: mismatch, signal: answering, after: prev.visit === v ? prev.after : undefined };
        });
      },
      (error: unknown) => {
        if (mine !== readGeneration.current || generation.current !== v) return;
        const code = failureReason(error);
        setRead((prev) => ({ visit: v, snapshot: prev.visit === v ? prev.snapshot : null, loading: false, error: code,
          signal: answering, after: prev.visit === v ? prev.after : undefined }));
      },
    );
  }, [portfolio]);

  /** Opens the context as visit `mine`, then reads it. The mount already
   * reads as opening; Retry resets the state in its handler first. */
  const start = useCallback(async (mine: number) => {
    try {
      const selected = await openContext(accountId);
      if (generation.current !== mine) return;
      portfolio.setSelection({ accountId: selected.accountId, ...selected.scope, view: 'own' });
      setContext({ ...selected, visit: mine });
      setOpening(false);
    } catch (e) {
      if (generation.current !== mine) return;
      setOpenError(errorCode(e));
      setOpening(false);
    }
  }, [openContext, accountId, portfolio]);
  const leave = useCallback(() => {
    generation.current++;
    portfolio.setSelection(null);
  }, [portfolio]);
  useEffect(() => {
    void start(++generation.current);
    return leave;
  }, [start, leave]);
  const retryOpen = useCallback(() => {
    const mine = ++generation.current;
    portfolio.setSelection(null);
    setContext(null);
    setOpening(true);
    setOpenError(null);
    void start(mine);
  }, [start, portfolio]);

  // The context's first read, and every re-read the host signals: state is
  // set only when the reply lands (a newer signal already reads as loading).
  const scope = context?.scope;
  useEffect(() => {
    if (!context || !scope) return;
    void readPortfolio(context.visit, scope, signal);
  }, [context, scope, signal, readPortfolio]);

  const release = useCallback((op: Pending) => {
    if (inFlight.current === op) {
      inFlight.current = null;
      setPending(null);
    }
    setNowMs(clock());
  }, [clock]);

  const after = read?.after;
  const refresh = useCallback((page: string | null | undefined = after) => {
    if (!context) return;
    if (inFlight.current && inFlight.current.visit === context.visit) return;
    const op: Pending = { key: '', kind: 'refresh', visit: context.visit };
    inFlight.current = op;
    setPending(op);
    setRead((prev) => (prev.visit === context.visit ? { ...prev, loading: true, error: null } : prev));
    void readPortfolio(context.visit, context.scope, signal, page ?? undefined).finally(() => release(op));
  }, [context, readPortfolio, release, signal, after]);

  // Kept live like the K/P lists: a quiet re-read on the host's cadence while
  // the page is visible. It never doubles a read or action in flight, and the
  // rows, row errors and an action in flight stay on screen through it.
  useEffect(() => {
    if (!autoRefreshMs || autoRefreshMs <= 0 || !context) return;
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refresh();
    }, autoRefreshMs);
    return () => clearInterval(timer);
  }, [autoRefreshMs, refresh, context]);

  const execute = useCallback((kind: UsRowOperation, row: UsOrderRow) => {
    if (!context) return;
    if (inFlight.current && inFlight.current.visit === context.visit) return;
    if (!row.orderId && kind !== 'status') return;
    const key = usOrderKey(row);
    const op: Pending = { key, kind, visit: context.visit };
    inFlight.current = op;
    setPending(op);
    const startedIn = context.visit;
    const request = { scope: row.scope, intentId: row.intentId };
    const call = kind === 'status'
      ? portfolio.checkOrderStatus(request).then((order) => (s: UsPortfolioSnapshot) => applyUsOrderStatus(s, order))
      : (kind === 'cancel'
        ? portfolio.cancelOrder({ ...request, orderId: row.orderId! })
        : portfolio.repeatCancel({ ...request, orderId: row.orderId!, previousAttemptId: row.cancel!.attemptId })
      ).then((result) => (s: UsPortfolioSnapshot) => applyUsCancelResult(s, result));
    call.then(
      (apply) => {
        // Both writes are tagged with the visit that asked; a later visit never shows them.
        setRead((prev) => {
          if (prev.visit !== startedIn || !prev.snapshot) return prev;
          const next = apply(prev.snapshot);
          return { ...prev, snapshot: next, keep: finishedSince(prev.snapshot, next, prev.keep) };
        });
        setErrors((prev) => {
          if (prev.visit !== startedIn || !prev.map.has(key)) return prev;
          const map = new Map(prev.map);
          map.delete(key);
          return { visit: prev.visit, map };
        });
      },
      (error: unknown) => {
        if (generation.current !== startedIn) return;
        const code = failureReason(error);
        // The row stays exactly as it was: an error never erases it.
        setErrors((prev) => ({ visit: startedIn, map: new Map(prev.visit === startedIn ? prev.map : NO_ERRORS).set(key, code) }));
      },
    ).finally(() => release(op));
  }, [context, portfolio, release]);
  // R8-F1: a cancel asks first when the host says so; everything else, and a
  // confirmed cancel, goes straight to `execute`. The same lock applies: an
  // operation already in flight is not asked about.
  const run = useCallback((kind: UsRowOperation, row: UsOrderRow) => {
    if (!context) return;
    if (confirmCancel && (kind === 'cancel' || kind === 'repeat') && row.orderId) {
      if (inFlight.current && inFlight.current.visit === context.visit) return;
      setAskedCancel({ kind, row, visit: context.visit });
      return;
    }
    execute(kind, row);
  }, [confirmCancel, execute, context]);
  const confirmBack = useCallback(() => setAskedCancel(null), []);
  const confirmGo = useCallback(() => {
    const target = pendingConfirm;
    setAskedCancel(null);
    if (target) execute(target.kind, target.row);
  }, [pendingConfirm, execute]);

  // Tick once a second while a repeat cancel's wait is pending, so the
  // countdown in Cancel's title reads true and the last tick lands on the
  // unlock itself; no ticking otherwise.
  const orders = read?.snapshot?.orders ?? null;
  const readiness: UsReadiness | null = context?.readiness ?? null;
  useEffect(() => {
    if (!orders || !readiness) return;
    const now = clock();
    const next = orders
      .map((o) => usCancelView(o, readiness, now).repeatAvailableAtMs)
      .filter((at): at is number => at !== null && at > now)
      .sort((a, b) => a - b)[0];
    if (next === undefined) return;
    const timer = setTimeout(() => setNowMs(clock()), Math.min(1_000, next - now));
    return () => clearTimeout(timer);
  }, [orders, readiness, clock, nowMs]);

  // An action still settling from an earlier visit does not lock this one.
  const mine = pending && pending.visit === visit ? pending : null;
  const loading = !!context && (!read || read.loading || read.signal !== signal);
  const busy = mine !== null || loading;
  const snapshot = read?.snapshot ?? null;
  const keep = read?.keep;
  // Working orders only (a filled, canceled, expired or rejected order is
  // history), plus a row that finished under the trader's eyes this read and
  // any carrying a row error; holdings with something in them.
  const workingOrders = useMemo(() => (snapshot?.orders ?? NO_ROWS)
    .filter((r) => !presentUsOrder(r).terminal || usOrderNeedsFillVisibility(r) || keep?.has(r.intentId) || errors.has(usOrderKey(r))), [snapshot, keep, errors]);
  const openPositions = useMemo(() => (snapshot?.positions ?? NO_ROWS)
    .filter((r) => !usKnownZeroHolding(r) && !usPhantomHolding(r)), [snapshot]);
  // What Close may conclude from this read. A refresh in flight keeps the
  // shown read's verdict (Close waits on `busy`); a failed one does not.
  const readCurrent = !!snapshot && !read?.error;
  const morePages = read?.after !== undefined || !!snapshot?.readDetails?.nextOrderCursor;
  const ordersCoverage = snapshot?.readDetails ? (snapshot.readDetails.orders.state === 'unavailable' ? 'unavailable' : 'complete')
    : snapshot?.coverage.state;
  const ordersComplete = readCurrent && ordersCoverage === 'complete' && !morePages;
  const closeStates = useMemo<ReadonlyMap<string, UsCloseState>>(() => {
    void servingVersion;
    if (!context || !snapshot || openPositions.length === 0) return NO_CLOSE;
    const out = new Map<string, UsCloseState>();
    for (const position of openPositions) {
      out.set(usPositionKey(position), usCloseStateFor({
        position, orders: snapshot.orders, context, closed: closedAt.get(closedKey(context, position)),
        readCurrent, ordersComplete, served: marketServed?.(position.target.marketSlug) ?? null,
      }));
    }
    return out;
  }, [context, snapshot, openPositions, closedAt, readCurrent, ordersComplete, marketServed, servingVersion]);

  const edit = useCallback((order: UsOrderRow) => {
    if (context) setActing({ visit: context.visit, action: { kind: 'edit', order } });
  }, [context]);
  const close = useCallback((position: UsPositionRow, quantity: string) => {
    if (context) setActing({ visit: context.visit, action: { kind: 'close', position, quantity } });
  }, [context]);
  const refreshNow = useCallback(() => refresh(), [refresh]);
  const actions = useMemo<UsAccountActions>(() => ({ run, edit, close, refresh: refreshNow, retryOpen }),
    [run, edit, close, refreshNow, retryOpen]);

  const view = useMemo<UsAccountState>(() => ({
    accountId, ownership, label, context, visit, opening, openError,
    snapshot, loading, readError: read?.error ?? null, workingOrders, openPositions,
    busy, pending: mine ? { key: mine.key, kind: mine.kind } : null, errors, nowMs, closeStates,
  }), [accountId, ownership, label, context, visit, opening, openError, snapshot, loading, read?.error, workingOrders,
    openPositions, busy, mine, errors, nowMs, closeStates]);

  // The Edit/Close window, bound to the visit it was opened under.
  const action = context && acting?.visit === context.visit ? acting.action : null;
  const onDone = useCallback(() => {
    setActing(null);
    setActionRefresh((n) => n + 1);
  }, []);
  // The ticket recorded a request (a submit settled, a response was lost):
  // re-read the account. The window stays; only the trader closes it.
  const onRequestRecorded = useCallback(() => setActionRefresh((n) => n + 1), []);
  // An edit's replacement carries the order's pin (as Kalshi/international
  // edits do), and the orders are re-read at once.
  const editReplaced = useCallback((order: UsOrderRow) => (intentId: string) => {
    pinTransfer?.(usOrderPinKey(accountId, order.intentId), usOrderPinKey(accountId, intentId));
    setActionRefresh((n) => n + 1);
  }, [pinTransfer, accountId]);
  const closeEvent = useCallback((ctx: UsAccountContext, position: UsPositionRow, event: UsCloseEvent) => {
    const key = closedKey(ctx, position);
    setClosedAt((current) => {
      const next = new Map(current);
      if (event.phase === 'not-sent') next.delete(key);
      else next.set(key, { size: position.heldQuantity ?? '', intentId: event.phase === 'sent' ? event.intentId : null });
      return next;
    });
  }, []);
  const actingMount = useMemo<UsActionMount | null>(() => {
    if (!action || !context) return null;
    return action.kind === 'edit'
      ? { ...action, context, accountLabel: label, onRequestRecorded, onEditReplaced: editReplaced(action.order), onDone }
      : { ...action, context, accountLabel: label, onRequestRecorded,
        onCloseEvent: (event: UsCloseEvent) => closeEvent(context, action.position, event), onDone };
  }, [action, context, label, onDone, onRequestRecorded, editReplaced, closeEvent]);

  return {
    view,
    actions,
    confirm: pendingConfirm ? { kind: pendingConfirm.kind, row: pendingConfirm.row } : null,
    confirmBack,
    confirmGo,
    acting: actingMount,
  };
}

/** The pin key of a US order row on this account. */
export const usOrderRowPinKey = (accountId: string, row: Pick<UsOrderRow, 'intentId'>) => usOrderPinKey(accountId, row.intentId);
/** The pin key of a US holding row on this account. */
export const usHoldingRowPinKey = (accountId: string, row: Pick<UsPositionRow, 'target'>) =>
  usHoldingPinKey(accountId, row.target.marketSlug, row.target.outcome);

type ReaderProps = {
  accountId: string;
  ownership: 'personal' | 'pooled';
  label: string | null;
  openContext(accountId: string): Promise<UsAccountContext>;
  portfolio: () => Portfolio;
  refreshSignal: number;
  autoRefreshMs?: number;
  confirmCancel: boolean;
  marketServed?: (marketSlug: string) => UsMarketServed;
  servingVersion?: number;
  pinTransfer?(previousKey: string, replacementKey: string): void;
  onView(accountId: string, view: UsAccountView | null): void;
};

/** One account's reader: reports its view to the feed before paint (a
 * passive effect would leave the Tracker's Cancel visible holding the previous
 * rows after the DOM narrowed), and renders only its dialogs. */
function UsAccountReader({ accountId, ownership, label, openContext, portfolio: makePortfolio, refreshSignal, autoRefreshMs, confirmCancel,
  marketServed, servingVersion, pinTransfer, onView }: ReaderProps) {
  const [portfolio] = useState(makePortfolio);
  const { view, actions, confirm, confirmBack, confirmGo, acting } = useUsAccountRows({
    accountId, ownership, label, openContext, portfolio, refreshSignal, autoRefreshMs, confirmCancel, marketServed, servingVersion, pinTransfer,
  });
  useLayoutEffect(() => {
    onView(accountId, { ...view, actions });
  }, [onView, accountId, view, actions]);
  useEffect(() => () => onView(accountId, null), [onView, accountId]);
  return (
    <>
      {/* R8-F1: the Tracker's own confirmation, for this account's rows (every layout runs through `run`). */}
      <ConfirmModal
        isOpen={confirm !== null}
        onClose={confirmBack}
        onConfirm={confirmGo}
        title="Cancel order?"
        message={confirm ? usCancelConfirmText(confirm.kind, confirm.row, label ?? '') : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />
      {acting && <UsActionWindow key={`${acting.kind}:${view.visit}:${acting.kind === 'edit' ? acting.order.intentId
        : `${acting.position.target.marketSlug}|${acting.position.target.outcome}`}`} mount={acting} />}
    </>
  );
}

type Listed = { rows: UsVisibleAccount[]; complete: boolean };

/** Every visible account, across pages (bounded as the rail's read is). */
async function listAll(accounts: Accounts): Promise<Listed> {
  const rows: UsVisibleAccount[] = [];
  let after: string | undefined;
  for (let page = 0; ; page++) {
    const result = await accounts.listAccounts(after);
    rows.push(...result.accounts);
    if (!result.nextCursor) return { rows, complete: true };
    if (page + 1 >= US_BULK_MAX_ACCOUNT_PAGES) return { rows, complete: false };
    after = result.nextCursor;
  }
}

export type UsTrackerFeedProps = {
  sessionKey: string;
  readiness: VenueReadiness;
  /** Bumped after the Tracker's Cancel all touched US orders: re-read them. */
  refreshSignal: number;
  confirmCancel: boolean;
  autoRefreshMs?: number;
  marketServed?: (marketSlug: string) => UsMarketServed;
  servingVersion?: number;
  pinTransfer?(previousKey: string, replacementKey: string): void;
  /** The pooled read, or null while US is not readable on this login. */
  onRead(read: UsTrackerRead | null): void;
  /** Test seam: the clients (the browser clients by default). */
  clients?: { accounts: () => Accounts; portfolio: () => Portfolio };
};

const browserClients = { accounts: createUsBrowserAccountClient, portfolio: createUsBrowserPortfolioClient };

/** Mounts one reader per own-view account once the login's US account list
 * is readable; a new session key starts over. Renders only the readers'
 * dialogs. */
export function UsTrackerFeed({ sessionKey, readiness, onRead, ...rest }: UsTrackerFeedProps) {
  const readable = readiness.readable;
  useEffect(() => {
    if (!readable) onRead(null);
  }, [readable, onRead]);
  if (!readable) return null;
  return (
    <>
      {/* The one mark that US is admitted on this page (the readers render only their dialogs). */}
      <span hidden data-testid="tracker-us-feed" />
      <SessionFeed key={sessionKey} onRead={onRead} {...rest} />
    </>
  );
}

function SessionFeed({ refreshSignal, confirmCancel, autoRefreshMs = US_TRACKER_POLL_MS, marketServed, servingVersion, pinTransfer, onRead,
  clients = browserClients }: Omit<UsTrackerFeedProps, 'sessionKey' | 'readiness'>) {
  const [accounts] = useState(clients.accounts);
  const generation = useRef(0);
  const [listed, setListed] = useState<Listed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The list read sets state only when its answer lands; a mount already
  // reads as loading, and Retry marks itself loading before calling.
  const readList = useCallback(() => {
    const mine = ++generation.current;
    listAll(accounts).then((result) => {
      if (generation.current !== mine) return;
      setListed(result);
      setLoading(false);
    }, (e) => {
      if (generation.current !== mine) return;
      setError(errorCode(e));
      // No account remains readable after a failed permission-list read.
      setListed(null);
      setLoading(false);
    });
  }, [accounts]);
  const retryList = useCallback(() => {
    setLoading(true);
    setError(null);
    readList();
  }, [readList]);
  // The account client answers one read at a time (a newer read invalidates
  // an older one in flight), so readers open their contexts in turn. A
  // refused context does not hold up the next.
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const openContext = useCallback((accountId: string) => {
    const next = chain.current.then(() => accounts.selectAccount(accountId, 'own'));
    chain.current = next.catch(() => undefined);
    return next;
  }, [accounts]);
  const invalidate = useCallback(() => {
    generation.current++;
    accounts.invalidate();
  }, [accounts]);
  useEffect(() => {
    readList();
    return invalidate;
  }, [readList, invalidate]);

  const own = useMemo(() => listed?.rows.filter((row) => row.canViewOwn) ?? [], [listed]);
  const [views, setViews] = useState<ReadonlyMap<string, UsAccountView>>(new Map());
  const onView = useCallback((accountId: string, view: UsAccountView | null) => {
    setViews((prev) => {
      if (view ? prev.get(accountId) === view : !prev.has(accountId)) return prev;
      const next = new Map(prev);
      if (view) next.set(accountId, view);
      else next.delete(accountId);
      return next;
    });
  }, []);
  const read = useMemo<UsTrackerRead>(() => ({
    accounts: own.map((row) => views.get(row.accountId)).filter((v): v is UsAccountView => !!v),
    ownAccounts: own.length,
    list: loading || (!listed && !error) ? { state: 'loading' } : listed ? { state: 'listed', complete: listed.complete }
      : { state: 'failed', code: error ?? 'US_READ_UNAVAILABLE' },
    retryList,
  }), [own, views, loading, listed, error, retryList]);
  useLayoutEffect(() => {
    onRead(read);
  }, [onRead, read]);
  useEffect(() => () => onRead(null), [onRead]);

  return <>
    {own.map((row, index) => (
      <UsAccountReader key={row.accountId} accountId={row.accountId} ownership={row.ownership}
        label={own.length > 1 ? usAccountLabel(row.ownership, index, own.length) : null}
        openContext={openContext} portfolio={clients.portfolio} refreshSignal={refreshSignal} autoRefreshMs={autoRefreshMs}
        confirmCancel={confirmCancel} marketServed={marketServed} servingVersion={servingVersion} pinTransfer={pinTransfer} onView={onView} />
    ))}
  </>;
}
